import { useEffect, useState, useMemo } from "react";
import papi, { formatErr } from "@/lib/partnerApi";
import { SectionHead, BandBadge, StatusBadge, Val, isVal } from "./VdaShared";
import { Loader2, Phone, Globe } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Discovered", "Verified", "Contacted", "Onboarded", "Rejected"];

export default function VdaVerification() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("Discovered");

  const load = () => papi.get("/vda/prospects").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = {}; STATUSES.forEach((s) => c[s] = 0);
    (rows || []).forEach((r) => { const s = r.verification_status || "Discovered"; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [rows]);

  const list = (rows || []).filter((r) => (r.verification_status || "Discovered") === tab);

  const update = async (vid, status) => {
    try { await papi.put(`/vda/prospects/${vid}/status`, { verification_status: status });
      setRows((rs) => rs.map((r) => r.id === vid ? { ...r, verification_status: status } : r));
      toast.success(`Moved to ${status}`); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <SectionHead title="Verification" desc="Review and progress prospects through the onboarding pipeline." />

      <div className="mb-5 flex flex-wrap gap-2" data-testid="verify-tabs">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`verify-tab-${s}`} onClick={() => setTab(s)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-teal-300"}`}>
            {s} <span className="ml-1 opacity-60">{counts[s] || 0}</span>
          </button>
        ))}
      </div>

      {rows === null ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>
        : list.length === 0 ? <div className="flex h-48 items-center justify-center rounded-2xl border border-slate-200 bg-white text-sm text-slate-400">No prospects in “{tab}”.</div>
        : (
          <div className="space-y-3" data-testid="verify-list">
            {list.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="min-w-[200px] flex-1">
                  <p className="font-semibold text-slate-800">{r.business_name}</p>
                  <p className="text-xs text-slate-400">{r.category} · {r.city}</p>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-600">
                  {isVal(r.phone) && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-teal-500" />{r.phone}</span>}
                  {isVal(r.website) && <Val v={r.website} href />}
                </div>
                <BandBadge band={r.score_band} />
                <span className="text-xs tabular-nums text-slate-500">Score {r.discovery_score}</span>
                <select data-testid={`verify-status-${r.id}`} value={r.verification_status || "Discovered"} onChange={(e) => update(r.id, e.target.value)}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-sm">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
